import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  User, Mail, AtSign, Lock, Bell, BellRing, Palette, LifeBuoy, LogOut, Trash2,
  ShieldCheck, ChevronRight, Flag, MessageSquare,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/Button";
import { Modal, Field, TextInput, TextArea } from "@/components/Modal";
import { pushSupported, getPushStatus, enablePush, disablePush, sendTestPush } from "@/lib/push";

function Row({ icon: Icon, label, value, onClick, danger, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-white/5 transition-colors text-left">
      <Icon size={18} className={danger ? "text-red-400" : "text-sky-400"} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${danger ? "text-red-400" : "text-slate-200"}`}>{label}</p>
        {value && <p className="text-xs text-slate-500 truncate">{value}</p>}
      </div>
      <ChevronRight size={16} className="text-slate-600" />
    </button>
  );
}

export default function SettingsPage() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const notif = user?.settings?.notifications || { connections: true, shared: true, reminders: true, email: true };

  const openModal = (name, init = {}) => { setForm(init); setModal(name); };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setModal(null); await refresh(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const toggleNotif = async (key) => {
    const next = { ...notif, [key]: !notif[key] };
    await api.put("/account/notifications", { settings: next });
    await refresh();
  };

  const [push, setPush] = useState({ supported: pushSupported, this_device: false });
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    getPushStatus().then(setPush).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.this_device) {
        await disablePush();
        toast.success("Push disabled on this device");
      } else {
        await enablePush();
        toast.success("Push enabled on this device");
      }
      setPush(await getPushStatus());
    } catch (e) {
      toast.error(e.message || "Couldn't update push notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const [emailBusy, setEmailBusy] = useState(false);
  const testEmail = async () => {
    setEmailBusy(true);
    try {
      await api.post("/account/test-email");
      toast.success(`Test email sent to ${user.email}. Check your Spam/Junk folder too.`, { duration: 8000 });
    } catch (e) { toast.error(apiErr(e)); } finally { setEmailBusy(false); }
  };

  const testPush = async () => {
    try {
      await sendTestPush();
      toast.success("Test notification sent");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="iks-fade-up max-w-2xl">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center"><User size={20} className="text-sky-400" /></div>
        <h1 className="font-display font-bold text-2xl text-slate-50">Settings</h1>
      </div>

      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Account</p>
      <div className="iks-card divide-y divide-white/5 mb-5 overflow-hidden">
        <Row icon={Mail} label="Email" value={user?.email + (user?.email_verified ? " · verified" : " · unverified")} onClick={() => openModal("email", { email: user.email })} testId="settings-email" />
        <Row icon={AtSign} label="Username" value={"@" + user?.username} onClick={() => openModal("username", { username: user.username })} testId="settings-username" />
        <Row icon={Lock} label="Password & security" value="Change your password" onClick={() => openModal("password", {})} testId="settings-password" />
        {!user?.email_verified && (
          <Row icon={ShieldCheck} label="Verify email" value="Resend verification link" onClick={() => run(() => api.post("/auth/resend-verification"), "Verification email sent")} testId="settings-verify" />
        )}
      </div>

      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Notifications</p>
      <div className="iks-card divide-y divide-white/5 mb-5 overflow-hidden">
        {[["connections", "Connection activity"], ["shared", "Shared space activity"], ["reminders", "Reminders"], ["email", "Email notifications"]].map(([k, l]) => (
          <div key={k} className="flex items-center gap-3 px-4 py-3.5">
            <Bell size={18} className="text-sky-400" />
            <span className="flex-1 text-sm text-slate-200">{l}</span>
            {k === "email" && (
              <button onClick={testEmail} disabled={emailBusy} data-testid="email-test-btn" className="text-xs text-sky-400 hover:text-sky-300 mr-1 disabled:opacity-50">{emailBusy ? "Sending…" : "Test"}</button>
            )}
            <button onClick={() => toggleNotif(k)} data-testid={`notif-toggle-${k}`} className={`relative w-11 h-6 rounded-full transition-colors ${notif[k] ? "bg-blue-600" : "bg-slate-700"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${notif[k] ? "translate-x-5" : ""}`} />
            </button>
          </div>
        ))}
        {push.supported && (
          <div className="flex items-center gap-3 px-4 py-3.5">
            <BellRing size={18} className="text-sky-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-200">Push notifications</p>
              <p className="text-xs text-slate-500">
                {push.this_device ? "On for this device" : "Get alerts on this device — dates, bills & requests"}
              </p>
            </div>
            {push.this_device && (
              <button onClick={testPush} data-testid="push-test-btn" className="text-xs text-sky-400 hover:text-sky-300 mr-1">Test</button>
            )}
            <button onClick={togglePush} disabled={pushBusy} data-testid="push-toggle" className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${push.this_device ? "bg-blue-600" : "bg-slate-700"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${push.this_device ? "translate-x-5" : ""}`} />
            </button>
          </div>
        )}
      </div>

      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Appearance</p>
      <div className="iks-card px-4 py-3.5 mb-5 flex items-center gap-3">
        <Palette size={18} className="text-sky-400" />
        <span className="flex-1 text-sm text-slate-200">Theme</span>
        <span className="text-xs text-slate-500">Midnight</span>
      </div>

      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Help & Support</p>
      <div className="iks-card divide-y divide-white/5 mb-5 overflow-hidden">
        <Row icon={MessageSquare} label="Contact Support" value="Get help from our team" onClick={() => openModal("support", {})} testId="settings-support" />
        <Row icon={Flag} label="Report a problem" value="Report a user or content" onClick={() => openModal("report", { target_type: "user", reason: "" })} testId="settings-report" />
        {user?.role === "admin" && <Row icon={ShieldCheck} label="Admin dashboard" onClick={() => nav("/admin")} testId="settings-admin" />}
      </div>

      <div className="iks-card divide-y divide-white/5 overflow-hidden">
        <Row icon={LogOut} label="Log out" onClick={() => run(logout, "Logged out")} testId="logout-btn" />
        <Row icon={Trash2} label="Delete account" danger onClick={() => openModal("delete", { password: "" })} testId="delete-account-row" />
      </div>

      {/* Modals */}
      <Modal open={modal === "email"} onClose={() => setModal(null)} title="Change email"
        footer={<><Button variant="secondary" full onClick={() => setModal(null)}>Cancel</Button><Button full loading={busy} onClick={() => run(() => api.put("/account/email", { email: form.email }), "Email updated")}>Save</Button></>}>
        <Field label="New email"><TextInput type="email" value={form.email || ""} onChange={(e) => setForm({ email: e.target.value })} data-testid="new-email" /></Field>
      </Modal>

      <Modal open={modal === "username"} onClose={() => setModal(null)} title="Change username"
        footer={<><Button variant="secondary" full onClick={() => setModal(null)}>Cancel</Button><Button full loading={busy} onClick={() => run(() => api.put("/account/username", { username: form.username }), "Username updated")}>Save</Button></>}>
        <Field label="New username"><TextInput value={form.username || ""} onChange={(e) => setForm({ username: e.target.value })} data-testid="new-username" /></Field>
      </Modal>

      <Modal open={modal === "password"} onClose={() => setModal(null)} title="Change password"
        footer={<><Button variant="secondary" full onClick={() => setModal(null)}>Cancel</Button><Button full loading={busy} onClick={() => run(() => api.put("/account/password", { current_password: form.current, new_password: form.next }), "Password changed")}>Save</Button></>}>
        <Field label="Current password"><TextInput type="password" value={form.current || ""} onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))} data-testid="current-password" /></Field>
        <Field label="New password"><TextInput type="password" value={form.next || ""} onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))} data-testid="new-password" /></Field>
      </Modal>

      <Modal open={modal === "support"} onClose={() => setModal(null)} title="Contact Support"
        footer={<><Button variant="secondary" full onClick={() => setModal(null)}>Cancel</Button><Button full loading={busy} onClick={() => run(() => api.post("/support/tickets", { subject: form.subject, message: form.message }), "Support ticket sent")}>Send</Button></>}>
        <Field label="Subject"><TextInput value={form.subject || ""} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} data-testid="ticket-subject" /></Field>
        <Field label="Message"><TextArea value={form.message || ""} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="ticket-message" /></Field>
      </Modal>

      <Modal open={modal === "report"} onClose={() => setModal(null)} title="Report a problem"
        footer={<><Button variant="secondary" full onClick={() => setModal(null)}>Cancel</Button><Button variant="destructive" full loading={busy} onClick={() => run(() => api.post("/reports", { target_type: form.target_type, target_id: form.target_id || "", reason: form.reason, description: form.description || "" }), "Report submitted")}>Submit</Button></>}>
        <Field label="What are you reporting?">
          <select value={form.target_type} onChange={(e) => setForm((f) => ({ ...f, target_type: e.target.value }))} className="iks-input w-full px-3.5 py-2.5 text-sm">
            {["user", "connection", "content", "other"].map((v) => <option key={v} value={v} className="bg-slate-900">{v}</option>)}
          </select>
        </Field>
        <Field label="Reason"><TextInput value={form.reason || ""} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} data-testid="report-reason" /></Field>
        <Field label="Description (optional)"><TextArea value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></Field>
      </Modal>

      <Modal open={modal === "delete"} onClose={() => setModal(null)} title="Delete account"
        footer={<><Button variant="secondary" full onClick={() => setModal(null)}>Cancel</Button><Button variant="destructive" full loading={busy} onClick={() => run(async () => { await api.post("/auth/delete-account", { password: form.password }); nav("/"); window.location.reload(); }, "Account deleted")} data-testid="confirm-delete-btn">Delete forever</Button></>}>
        <p className="text-sm text-slate-400 mb-4">This permanently deletes your personal data. Shared connection data is archived, not destroyed. Enter your password to confirm.</p>
        <Field label="Password"><TextInput type="password" value={form.password || ""} onChange={(e) => setForm({ password: e.target.value })} data-testid="delete-password" /></Field>
      </Modal>
    </div>
  );
}
